import { Note, CreateNoteRequest, UpdateNoteRequest, PublicNote } from '../types/notes';
import { apiCall } from './authApi';
import { handleResponse } from './todoApi';

export const notesApi = {
  getAllNotes: async (userEmail: string): Promise<Note[]> => {
    try {
      const response = await apiCall(`/notes?userEmail=${userEmail}`);
      return handleResponse(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'Authentication required') {
        throw error;
      }
      console.error('Failed to get notes:', error);
      return [];
    }
  },

  getNote: async (id: number): Promise<Note> => {
    const response = await apiCall(`/notes/${id}`);
    return handleResponse(response);
  },

  createNote: async (request: CreateNoteRequest, userEmail: string): Promise<Note> => {
    // Prepare the payload to match backend expectations
    const payload = {
      title: request.title || "",
      content: request.content,
      color: request.color || "#FFE066", // Default yellow color
      positionX: request.positionX || 100,
      positionY: request.positionY || 100,
      width: request.width || 250,
      height: request.height || 200,
      tags: Array.isArray(request.tags) ? request.tags.join(',') : request.tags, // Convert array to comma-separated string
      createdBy: userEmail // Backend expects 'createdBy', not 'userEmail'
    };
    
    console.log('🚀 Sending createNote payload:', payload);
    
    const response = await apiCall('/notes', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return handleResponse(response);
  },

  updateNote: async (id: number, request: UpdateNoteRequest, userEmail: string): Promise<Note> => {
    // Convert tags array to comma-separated string for backend
    const payload = {
      ...request,
      tags: Array.isArray(request.tags) ? request.tags.join(',') : request.tags
    };
    
    const response = await apiCall(`/notes/${id}?userEmail=${userEmail}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    return handleResponse(response);
  },

  deleteNote: async (id: number): Promise<void> => {
    const response = await apiCall(`/notes/${id}`, {
      method: 'DELETE'
    });
    await handleResponse(response);
  },

  getPublicNote: async (token: string): Promise<PublicNote> => {
    // Use direct fetch for public endpoint (no auth needed)
    const baseUrl = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_BASE_URL;
    const response = await fetch(`${baseUrl}/notes/public/${token}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch public note: ${response.statusText}`);
    }
    return response.json();
  },

  shareNote: async (id: number, userEmail: string): Promise<Note> => {
    const response = await apiCall(`/notes/${id}/share?userEmail=${userEmail}`, {
      method: 'POST'
    });
    return handleResponse(response);
  },

  unshareNote: async (id: number, userEmail: string): Promise<Note> => {
    const response = await apiCall(`/notes/${id}/share?userEmail=${userEmail}`, {
      method: 'DELETE'
    });
    return handleResponse(response);
  }
};